import { FrontendUserSchema } from '@buyerflo/core/schemas/userMembership'
import { useUserName, useUsersApi } from 'apiProviders'
import { Autocomplete, AutocompleteProps } from 'components/autocomplete'
import { T, omit, prop } from 'ramda'
import { forwardRef } from 'react'

interface Props<
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> extends Omit<AutocompleteProps<string, Multiple, DisableClearable, FreeSolo>, 'options' | 'onChange'> {
    onChange?: (agent: string | null) => void
    filter?: (user: FrontendUserSchema) => boolean
}

export default forwardRef(
    <
        Multiple extends boolean | undefined,
        DisableClearable extends boolean | undefined,
        FreeSolo extends boolean | undefined,
    >(
        { filter, value, onChange, ...props }: Props<Multiple, DisableClearable, FreeSolo>,
        ref: any,
    ) => {
        const usersApi = useUsersApi(true)
        const userName = useUserName()

        return (
            <Autocomplete
                inputRef={ref}
                options={(usersApi.data ?? [])
                    .filter(filter ?? T)
                    .map(prop('userId'))}
                getOptionLabel={userName}
                value={value ?? (null as any)}
                onChange={(e, value) => {
                    onChange?.(value as any)
                }}
                {...props}
            />
        )
    },
)

interface MultipleProps<DisableClearable extends boolean | undefined>
    extends Omit<AutocompleteProps<string, true, DisableClearable, false>, 'options' | 'onChange'> {
    onChange?: (agents: string[]) => void
    getOptionDisabled?: (userId: string) => boolean
    filter?: (user: FrontendUserSchema) => boolean
}

export const MultipleAgentInput = forwardRef(
    <DisableClearable extends boolean | undefined>(
        { filter, value, onChange, ...props }: MultipleProps<DisableClearable>,
        ref: any,
    ) => {
        const usersApi = useUsersApi(true)

        return (
            <Autocomplete
                inputRef={ref}
                {...omit(['key'], props)}
                multiple
                options={(usersApi.data ?? [])
                    .filter(filter ?? T)
                    .map(prop('userId'))}
                getOptionLabel={(userId: string) => {
                    const user = usersApi.data?.find(user => user.userId === userId)
                    return user ? `${user.firstName} ${user.lastName}` : 'Loading...'
                }}
                value={value}
                onChange={(e, value) => {
                    onChange && onChange(value as string[])
                }}
            />
        )
    },
)
